"use client";

import { useEffect } from "react";
import { notifyMutationError } from "@/utils/mutationError";
import {
  isServer,
  QueryClient,
  QueryCache,
  MutationCache,
  QueryClientProvider,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslations } from "next-intl";
import { reportError } from "@/lib/errorReporter";
import { initChunkReloadGuard } from "@/lib/chunkReloadGuard";
import {
  isTransientNetworkError,
  buildApiErrorReport,
} from "@/api/classifyApiError";
import { useSocketStore } from "@/stores/SocketStore";

// Retry a transient network blip a few times before giving up. Non-network
// failures (5xx/4xx) are not retried here — they are handled/reported at once.
const MAX_NETWORK_RETRIES = 3;

// Runs only once React Query has EXHAUSTED every retry (queryCache/mutationCache
// onError fires after the final attempt). A brief blip that recovers on retry
// therefore never reaches the monitor; a lasting outage still does — the report
// the interceptor used to emit per-attempt now happens once, at the end.
const reportNetworkFailure = (error: unknown): void => {
  if (!isTransientNetworkError(error)) return;
  const socket = useSocketStore.getState().socket;
  const report = buildApiErrorReport(
    error as AxiosError,
    { connected: socket.connected, id: socket.id ?? null },
    null
  );
  reportError(report);
};

interface Props {
  children: React.ReactNode;
}

/**
 * Mutation hatalarını sonner'ın native error toast'ı ile gösterir.
 * Toaster CSS'i Zoro-pattern (solid bg, close, progress bar) zaten uygular —
 * burada custom render YOK, tüm toast'lar tek tip görünür.
 */
const onErrorCallback = (t: any, error: Error): void => {
  notifyMutationError(t, error);
};

// `t` is threaded in as a plain argument (not called here) so this function
// stays a regular helper, not a hook — it is invoked conditionally below
// (only when the cached client is missing), and a hook can never be called
// conditionally (Rules of Hooks). Previously this called `useTranslations()`
// internally, which made the hook run on the render that creates the client
// but skip it on every later render that reuses `browserQueryClient` —
// a hook-count mismatch that crashed the whole app on any re-render of
// `TanstackProviders` after the first.
const makeQueryClient = (t: ReturnType<typeof useTranslations>) => {
  return new QueryClient({
    // Cache-level onError fires after the LAST retry — the single place a
    // fully-failed transient network drop is reported (see reportNetworkFailure).
    queryCache: new QueryCache({ onError: reportNetworkFailure }),
    mutationCache: new MutationCache({ onError: reportNetworkFailure }),
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        // Only transient network blips retry; a server-answered failure (5xx/4xx)
        // is not retried here. Per-query overrides (e.g. retry: false) still win.
        retry: (failureCount, error) =>
          isTransientNetworkError(error) && failureCount < MAX_NETWORK_RETRIES,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnReconnect: true, // phone regains internet -> auto refresh
        refetchOnWindowFocus: true, // mobile tab returns to foreground -> refresh
      },
      mutations: {
        // Never auto-retry mutations — retrying a coupon submission risks a
        // double-placed coupon. Report path is the mutationCache onError above.
        retry: 0,
        onError: (error) => onErrorCallback(t, error),
      },
    },
  });
};

let browserQueryClient: QueryClient | undefined = undefined;

export const getQueryClient = (t: ReturnType<typeof useTranslations>) => {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient(t);
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient(t);
    return browserQueryClient;
  }
};

const TanstackProviders: React.FC<Props> = ({ children }) => {
  // Called unconditionally, every render — the ONLY hook-safe way to feed a
  // translator into the client below (see the comment on `makeQueryClient`).
  const t = useTranslations();

  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient(t);

  // The error reporter starts in src/instrumentation-client.ts, before any
  // page code, so it also catches crashes while a page's modules load.
  useEffect(() => {
    // Auto-reload once when a lazy page chunk fails to load (stale build after
    // a deploy / transient network drop) instead of stranding the user on the
    // Next.js "Application error" screen. The reporter still logs it.
    initChunkReloadGuard();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export { TanstackProviders };
